import api from './api';

class CRMService {
    constructor() {
        this.baseURL = '/crm/api';
    }

    // Lead Management
    async getLeads(params = {}) {
        const response = await api.get(`${this.baseURL}/leads/`, { params });
        return response.data;
    }

    async getLead(id) {
        const response = await api.get(`${this.baseURL}/leads/${id}/`);
        return response.data;
    }

    async createLead(data) {
        const response = await api.post(`${this.baseURL}/leads/`, data);
        return response.data;
    }

    async updateLead(id, data) {
        const response = await api.patch(`${this.baseURL}/leads/${id}/`, data);
        return response.data;
    }

    async deleteLead(id) {
        await api.delete(`${this.baseURL}/leads/${id}/`);
    }

    async convertLeadToCustomer(id, customerId) {
        const response = await api.post(`${this.baseURL}/leads/${id}/convert_to_customer/`, {
            customer_id: customerId
        });
        return response.data;
    }

    async getLeadDetail(id) {
        const response = await api.get(`${this.baseURL}/leads/${id}/`);
        return response.data;
    }

    async getCustomerEvents(id) {
        const response = await api.get(`${this.baseURL}/events/`, {
            params: { customer: id }
        });
        return response.data;
    }

    async getContactSummary(recordType, recordId) {
        const response = await api.get(`${this.baseURL}/contact-summary/${recordType}/${recordId}/`);
        return response.data;
    }

    async getEmailReplies(params) {
        const response = await api.get(`${this.baseURL}/email-replies/`, { params });
        return response.data;
    }

    async getLeadTimeline(id) {
        const response = await api.get(`${this.baseURL}/leads/${id}/timeline/`);
        return response.data;
    }

    async getLeadTasks(id) {
        const response = await api.get('/organizer/api/tasks/', {
            params: { related_entity_type: 'lead', related_entity_id: id }
        });
        return response.data;
    }

    async getLeadEvents(id) {
        const response = await api.get(`${this.baseURL}/events/`, {
            params: { lead: id, page_size: 500 }
        });
        return response.data;
    }

    async getLeadContacts(id) {
        const response = await api.get(`${this.baseURL}/contacts/by_entity/`, {
            params: { entity_type: 'lead', entity_id: id }
        });
        return response.data;
    }

    async getLeadNotes(id) {
        return { results: [] };
    }

    async getLeadDashboardStats() {
        const response = await api.get(`${this.baseURL}/leads/dashboard_stats/`);
        return response.data;
    }

    async searchLeadsAndCustomers(query) {
        const response = await api.get(`${this.baseURL}/search/`, { 
            params: { q: query } 
        });
        return response.data;
    }

    async getNearbyLeads(latitude, longitude, radius = 5) {
        const response = await api.get(`${this.baseURL}/leads/nearby/`, {
            params: { latitude, longitude, radius }
        });
        return response.data;
    }

    async getSalesZones() {
        const response = await api.get('/customers/sales-zones/');
        return response.data;
    }

    async assignLead(id, userId) {
        const response = await api.post(`${this.baseURL}/leads/${id}/assign/`, {
            user_id: userId
        });
        return response.data;
    }

    // Public Onboarding (no auth required — token-scoped)
    _raiseIfNotOk(response) {
        if (response.status < 200 || response.status >= 300) {
            const err = new Error(response.data?.detail || response.data?.error || `Request failed with status ${response.status}`);
            err.status = response.status;
            err.data = response.data;
            throw err;
        }
        return response.data;
    }

    async getOnboarding(token) {
        const response = await api.get(`${this.baseURL}/onboarding/${token}/`);
        return this._raiseIfNotOk(response);
    }

    async saveOnboarding(token, data) {
        const response = await api.put(`${this.baseURL}/onboarding/${token}/`, { data });
        return this._raiseIfNotOk(response);
    }

    async uploadOnboardingDocument(token, docType, file) {
        const formData = new FormData();
        formData.append('doc_type', docType);
        formData.append('file', file);
        const response = await api.post(`${this.baseURL}/onboarding/${token}/documents/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return this._raiseIfNotOk(response);
    }

    async deleteOnboardingDocument(token, docId) {
        const response = await api.delete(`${this.baseURL}/onboarding/${token}/documents/${docId}/`);
        return this._raiseIfNotOk(response);
    }

    async submitOnboarding(token, signedName, data = null) {
        const payload = { signed_name: signedName, agree: true };
        if (data) {
            payload.data = data;
        }
        const response = await api.post(`${this.baseURL}/onboarding/${token}/submit/`, payload);
        return this._raiseIfNotOk(response);
    }

    // Operator: Lead Onboarding Application
    async inviteOnboarding(leadId) {
        const response = await api.post(`${this.baseURL}/leads/${leadId}/invite-onboarding/`, {
            base_url: window.location.origin
        });
        return this._raiseIfNotOk(response);
    }

    async getLeadApplication(leadId) {
        const response = await api.get(`${this.baseURL}/leads/${leadId}/application/`);
        if (response.status === 404) {
            return null;
        }
        return this._raiseIfNotOk(response);
    }

    async requestApplicationChanges(leadId, note) {
        const response = await api.post(`${this.baseURL}/leads/${leadId}/application/request-changes/`, {
            note,
            base_url: window.location.origin
        });
        return this._raiseIfNotOk(response);
    }

    async generateCustomer(leadId, customerName = null, force = false) {
        const payload = { base_url: window.location.origin };
        if (customerName) {
            payload.customer_name = customerName;
        }
        if (force) {
            payload.force = true;
        }
        const response = await api.post(`${this.baseURL}/leads/${leadId}/generate-customer/`, payload);
        return this._raiseIfNotOk(response);
    }

    async updateCustomerCredentials(leadId, username, password) {
        const payload = {};
        if (username) payload.username = username;
        if (password) payload.password = password;
        const response = await api.post(`${this.baseURL}/leads/${leadId}/update-customer-credentials/`, payload);
        return this._raiseIfNotOk(response);
    }

    async verifyLeadDocument(docId) {
        const response = await api.post(`${this.baseURL}/lead-documents/${docId}/verify/`);
        return this._raiseIfNotOk(response);
    }

    async uploadLeadApplicationDocument(leadId, docType, file) {
        const formData = new FormData();
        formData.append('doc_type', docType);
        formData.append('file', file);
        const response = await api.post(`${this.baseURL}/leads/${leadId}/application/documents/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return this._raiseIfNotOk(response);
    }

    async downloadLeadDocument(docId) {
        const response = await api.get(`${this.baseURL}/lead-documents/${docId}/download/`, {
            responseType: 'blob'
        });
        if (response.status >= 400) {
            throw new Error(`Failed to download document (status ${response.status})`);
        }
        return response.data;
    }

    // Event Management (interaction log for leads and customers)
    async getEvents(params = {}) {
        const response = await api.get(`${this.baseURL}/events/`, { params });
        return response.data;
    }

    async getEvent(id) {
        const response = await api.get(`${this.baseURL}/events/${id}/`);
        return response.data;
    }

    async createEvent(data) {
        const response = await api.post(`${this.baseURL}/events/`, data);
        return response.data;
    }

    async updateEvent(id, data) {
        const response = await api.patch(`${this.baseURL}/events/${id}/`, data);
        return response.data;
    }

    async deleteEvent(id) {
        await api.delete(`${this.baseURL}/events/${id}/`);
    }

    async quickLogEvent(data) {
        const response = await api.post(`${this.baseURL}/events/quick_log/`, data);
        return response.data;
    }

    async getEventStats() {
        const response = await api.get(`${this.baseURL}/events/stats/`);
        return response.data;
    }

    async getRecentEvents(limit = 10) {
        const response = await api.get(`${this.baseURL}/events/recent/`);
        return response.data;
    }

    async getFollowUpEvents() {
        const response = await api.get(`${this.baseURL}/events/follow_ups/`);
        return response.data;
    }

    async markEventFollowedUp(id) {
        const response = await api.post(`${this.baseURL}/events/${id}/mark_followed_up/`);
        return response.data;
    }

    async getEventsByLead(leadId) {
        const response = await api.get(`${this.baseURL}/events/by_lead/?lead_id=${leadId}`);
        return response.data;
    }

    async deleteContact(id) {
        await api.delete(`${this.baseURL}/contacts/${id}/`);
    }

    async quickAddContact(data) {
        const response = await api.post(`${this.baseURL}/contacts/quick_add/`, data);
        return response.data;
    }

    async getContactVisitHistory(id) {
        const response = await api.get(`${this.baseURL}/contacts/${id}/visit_history/`);
        return response.data;
    }

    async getDecisionMakers() {
        const response = await api.get(`${this.baseURL}/contacts/decision_makers/`);
        return response.data;
    }

    async getContactsByEntity(entityType, entityId) {
        const response = await api.get(`${this.baseURL}/contacts/by_entity/`, {
            params: { entity_type: entityType, entity_id: entityId }
        });
        return response.data;
    }

    async getContactDashboardStats() {
        const response = await api.get(`${this.baseURL}/contacts/dashboard_stats/`);
        return response.data;
    }

    async getContactSearchSuggestions(query) {
        const response = await api.get(`${this.baseURL}/contacts/search_suggestions/`, {
            params: { q: query }
        });
        return response.data;
    }

    async updateContactRelationship(id, relationshipQuality, personalNotes = '') {
        const response = await api.post(`${this.baseURL}/contacts/${id}/update_relationship/`, {
            relationship_quality: relationshipQuality,
            personal_notes: personalNotes
        });
        return response.data;
    }

    async toggleContactActive(id) {
        const response = await api.post(`${this.baseURL}/contacts/${id}/toggle_active/`);
        return response.data;
    }

    async exportContactData() {
        const response = await api.get(`${this.baseURL}/contacts/export_data/`);
        return response.data;
    }

    // Email Templates
    async getEmailTemplates(params = {}) {
        const response = await api.get(`${this.baseURL}/email-templates/`, { params });
        return response.data;
    }

    async getEmailTemplate(id) {
        const response = await api.get(`${this.baseURL}/email-templates/${id}/`);
        return response.data;
    }

    async createEmailTemplate(data) {
        const response = await api.post(`${this.baseURL}/email-templates/`, data);
        return response.data;
    }

    async updateEmailTemplate(id, data) {
        const response = await api.patch(`${this.baseURL}/email-templates/${id}/`, data);
        return response.data;
    }

    async deleteEmailTemplate(id) {
        await api.delete(`${this.baseURL}/email-templates/${id}/`);
    }

    // CRM Emails
    async getSentEmails(params = {}) {
        const response = await api.get(`${this.baseURL}/emails/`, { params });
        return response.data;
    }

    async getSentEmail(id) {
        const response = await api.get(`${this.baseURL}/emails/${id}/`);
        return response.data;
    }

    async resendEmail(id) {
        const response = await api.post(`${this.baseURL}/emails/${id}/resend/`);
        return response.data;
    }

    async discardEmail(id) {
        await api.delete(`${this.baseURL}/emails/${id}/discard/`);
    }

    async getSignaturePreview(accountId) {
        const response = await api.get(`${this.baseURL}/emails/signature-preview/`, {
            params: { account_id: accountId || '' },
        });
        return response.data;
    }

    async sendEmail(data) {
        const response = await api.post(`${this.baseURL}/emails/send/`, data);
        return response.data;
    }

    // Utility Methods
    formatCurrency(amount) {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP'
        }).format(amount);
    }

    formatDate(dateString) {
        return new Date(dateString).toLocaleDateString('en-GB');
    }

    formatDateTime(dateString) {
        return new Date(dateString).toLocaleString('en-GB');
    }

    formatPhoneNumber(phone) {
        // Basic UK phone number formatting
        if (!phone) return '';
        return phone.replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3');
    }

    getStatusColor(status) {
        const colors = {
            // Lead statuses
            'new': 'bg-blue-100 text-blue-800',
            'contacted': 'bg-yellow-100 text-yellow-800',
            'qualified': 'bg-green-100 text-green-800',
            'negotiating': 'bg-orange-100 text-orange-800',
            'won': 'bg-emerald-100 text-emerald-800',
            'lost': 'bg-red-100 text-red-800',
            'on_hold': 'bg-gray-100 text-gray-800',
            
            // Task statuses
            'pending': 'bg-yellow-100 text-yellow-800',
            'in_progress': 'bg-blue-100 text-blue-800',
            'completed': 'bg-green-100 text-green-800',
            'cancelled': 'bg-red-100 text-red-800',
            'postponed': 'bg-gray-100 text-gray-800',
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    }

    getPriorityColor(priority) {
        const colors = {
            'urgent': 'bg-red-100 text-red-800',
            'high': 'bg-orange-100 text-orange-800',
            'normal': 'bg-blue-100 text-blue-800',
            'low': 'bg-gray-100 text-gray-800',
        };
        return colors[priority] || 'bg-gray-100 text-gray-800';
    }

    getRatingStars(rating) {
        return '★'.repeat(rating) + '☆'.repeat(5 - rating);
    }
}

const crmServiceInstance = new CRMService();
export default crmServiceInstance;